/* eslint-disable @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call */
import { getWebUrl } from '@anwb/browser';
import toKebabCase from '@anwb/to-kebab-case';
/**
 * Whether we're running inside an iframe
 */
const iframe = typeof window !== 'undefined' && window !== window.parent;
/**
 * When running inside an iframe we need to know the url to post to
 */
// eslint-disable-next-line @typescript-eslint/restrict-template-expressions
const url = `https://${getWebUrl()}`;
/**
 * This function will return wether the GTM's data-layer is inside of an iframe.
 * @returns {Bool}
 */
const isDataLayerInIframe = () => iframe &&
    window.location.hostname !== 'localhost' &&
    window.location.hostname.indexOf('services') === -1;
/**
 * This function will return wether the GTM's data-layer is available on production.
 * @returns {Bool}
 */
const isDataLayerAvailable = () => typeof window !== 'undefined' && !!window.dataLayer;
/**
 * This function will push events to the data layer. When in a non-production
 * environment, each data layer push will be logged within the console.
 * @param event an object representing the event
 * @param debuggingTag an optional tag that can be used to easily differentiate logs within the console.
 */
const dataLayer = (event, debuggingTag) => {
    if (isDataLayerInIframe()) {
        window.parent.postMessage({ type: 'data-layer', event }, url);
    }
    else if (isDataLayerAvailable()) {
        window.dataLayer.push(event);
    }
    if (process.env.NODE_ENV !== 'production') {
        const groupLabel = `📊 [DEBUG] Data layer push${debuggingTag ? `: ${debuggingTag}` : ''}`;
        /* eslint-disable no-console */
        console.groupCollapsed(groupLabel);
        console.log(event);
        console.groupEnd();
        /* eslint-enable no-console */
    }
};
/**
 * This will create an event to submit to google analytics
 * @param event           An object representing the event
 * @param event.type  	  A string representing the event's type
 * @param event.category  A string representing the event's category
 * @param event.action    A string representing the event's action
 * @param event.label     A string representing the event's label
 */
export const createFormEvent = ({ type, category, action, label, }) => ({
    event: type,
    eventCategory: category,
    eventAction: toKebabCase(action),
    eventLabel: toKebabCase(label),
});
/**
 * This function will push a formatted event specifically for components to the data layer
 * @param event           An object representing the event
 * @param event.type  	  A string representing the event's type
 * @param event.category  A string representing the event's category
 * @param event.action    A string representing the event's action
 * @param event.label     A string representing the event's label
 */
export const formDataLayer = ({ type, category, action, label, }) => dataLayer(createFormEvent({
    type,
    category,
    action,
    label,
}));
/**
    This function will push a data layer event in the format wanted by Google Analytics 4.
    @param event An object representing the event
 */
export const formDataLayerGA4 = (event) => dataLayer(Object.assign(Object.assign({}, event), (event.status ? { status: event.status.toString() } : {})));
/**
 * This function will push a formatted event specifically for components to the data layer
 * @param event           An object representing the event
 * @param event.type  	  A string representing the event's type
 * @param event.category  A string representing the event's category, which tells the data layer that this is an A/B/n test
 * @param event.action    A string representing the event's action,
 * @param event.label     A string representing the event's label, which corresponds to the test group the user is in
 */
export const abnDataLayer = ({ type, action, group, }) => dataLayer(createFormEvent({
    type,
    category: 'A/B/n test',
    action,
    label: group,
}));
/**
 * This function will push an event object to the data layer. When in a
 * non-production environment, each data layer push will be logged within the
 * console. The tag is used to easily differentiate logs within the console.
 *
 * @deprecated please use the dataLayer function in combination with the debuggingTag parameter.
 */
export const dataLayerPushTag = (object, debugGroupName) => {
    // Backwards compatible implementation.
    return dataLayer(object, debugGroupName);
};
export default dataLayer;
//# sourceMappingURL=data-layer.js.map